import moment from 'moment-timezone'; // moment-timezone package itself loads moment as a dependency.

export function addTrailingPeriod(string, maxLength) {
  const stripped = string.trim();

  if (stripped.substring(stripped.length - 1) === '.' || typeof maxLength === 'number' && stripped.length > maxLength) {
    return stripped;
  }

  return `${stripped}.`;
}
/**
 *
 * @param {string} salutation
 * @param {string} first
 * @param {string} last
 * @param {string} cred
 */

export function formatPhysicianName(salutation, first, last, cred) {
  /* eslint-disable indent */
  return first && last ? formatTemplate`${salutation ? `${addTrailingPeriod(salutation, 4)} ` : ''}${first} ${last}${cred ? `, ${cred}` : ''}` : '';
  /* eslint-enable */
}
/**
 * Creates a multi-line address object for display
 */

export function formatAddressMultiLine({
  street1,
  street2,
  city,
  state,
  zip,
  zip4
}) {
  const addressLine3 = flattenTemplate`${city ? `${city},` : ''}
                                      ${state ? `${state}` : ''}
                                      ${zip || ''}${zip4 ? `-${zip4}` : ''}`;
  const truthCheck = !!(street1 || addressLine3.length);
  return {
    addressLine1: truthCheck ? street1 || '' : 'Address not on file',
    addressLine2: truthCheck ? street2 || '' : '',
    addressLine3
  };
}
/**
 * Formats a date into a normalized view
 */

export const formatWeekDay = date => date ? moment(new Date(date)).format('ddd D') : '';
export const formatDateMMDD = date => date ? moment(new Date(date)).format('MM-DD') : '';
export const formatDateMMDDYYYY = date => date ? moment(new Date(date)).format('MM-DD-YYYY') : '';
export const formatLocDateMMDDYY = date => date ? moment(new Date(date)).format('MM/DD/YY') : '';
export const formatDay = date => date ? `${moment.utc(date).local().format('MM-DD-YY HH:mm - (dddd)')}` : '';
export const formatLocDateMMDDYYYY = date => date ? moment(new Date(date)).format('MM/DD/YYYY') : '';
export const formatDateYYYYMMDD = date => date ? moment(new Date(date)).format('YYYY-MM-DD') : '';
export const formatDateMMDDYY = date => date ? moment(new Date(date)).format('MM-DD-YY') : '';
export const formatPostDate = date => date ? `${moment.utc(date).format('YYYY-MM-DD HH:mm:ss')}` : '';
export const formatPimDate = date => date ? `${moment.utc(date).format('MM/DD/YYYY HH:mm:ss')}` : '';
export const formatAdmitPostDate = date => date ? `${moment.utc(date).format('YYYY-MM-DDTHH:mm:ss')}` : '';
export const formatDateTimeMMDD = date => date ? moment(new Date(date)).format('MM-DD-YY HH:mm') : '';
/**
 * Manifest passes a date string of format YYYY-MM-DD HH:MM:SS into
 * this date call.  Older versions of Safari's new Date() cannot parse
 * this date format and so will return invalid date.  Moment is able
 * to parse this format, even on old safari versions.  So in this case
 * pass the date string directly into moment instead of creating a new
 * date object first.
 * @param date
 * @returns
 */
// function used for UTC date time of shifteddatetime

export const formatUTCDateTimeMMDD = date => date ? moment.utc(date).format('MM-DD-YY HH:mm') : '';
export const formatDateTimeMMDDpm = date => date ? moment(date).format('MM-DD-YY hh:mm a') : '';
/**
 * Some API's are not sending shifted versions of the date in local time.
 */

export const convertUtcToLocal = date => moment(moment.utc(date).toDate()).local();
/**
 * Formats a UTC date back to local
 */

export const formatUTCLocalMMDDYY = date => {
  if (date) {
    const local = convertUtcToLocal(date).format('MM-DD-YY');
    return local;
  }

  return '';
};
export const formatLocaleDateYYYYMM = date => {
  if (date) {
    const utcDate = moment.utc(date).toDate();
    const local = moment(utcDate).local().format('YYYY-MM-DD');
    return local;
  }

  return '';
};
export const formatLocalDate = date => date ? `${moment.utc(date).local().format('YYYY-MM-DD HH:mm')}` : '';
export const formatLocalDateYYMMDD = date => date ? `${moment.utc(date).local().format('YY-MM-DD HH:mm')}` : '';
export const formatutcMMDDYY = (date, tz) => {
  if (date && tz) {
    return moment.utc(date).tz(tz).format('MM-DD-YY');
  }

  return '';
};
export const formatutcPrintMMDDYY = (date, tz) => {
  if (date && tz) {
    return moment.utc(date).tz(tz).format('MM/DD/YY');
  }

  return '';
};
/**
 * Pim sends birthday in timestamps. Convert to UTC
 * @param date
 */

export function formatDOBFromPim(date) {
  return date ? moment(date).utc().format('MM-DD-YYYY') : '';
}
/**
 * Formats 10 digit US phone number for display, with optional intl code.
 */

export function formatPhoneNumber(phoneNumberString) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return null;
}
/**
 * This function is meant to be used as a "template tag" with variables. This allows for us to
 * use template strings with proper code indentation. This will flatten strings to one line.
 *
 * Omits falsey values.
 *
 * Example usage:q
 * @example
 * const adjective = 'raggedy';
 * const flattened = flattenTemplate`So here is us, on the
 *                   ${adjective} edge. Don't push
 *                   me, and I won't push you.`;
 * // So here is us, on the raggedy edge. Don't push me, and I won't push you.
 * @ignore Credit to https://muffinresearch.co.uk/removing-leading-whitespace-in-es6-template-strings/
 *
 * @param strings Array of separated template strings
 * @param values Array of values given to template string.
 *
 * @return Flattened string with white space stripped.
 */

export function flattenTemplate(strings, ...values) {
  // Interweave the strings with the
  // substitution vars first.
  let output = '';
  values.forEach((val, i) => {
    output += (strings[i] !== undefined ? strings[i] : '') + (val !== undefined ? val : '');
  });
  output += strings[values.length]; // Split on newlines.

  const lines = output.split(/(?:\r\n|\n|\r)/); // Rip out the leading whitespace.

  return lines.map(line => line.replace(/^\s+/gm, '')).join(' ').trim();
}
/**
 * Modified from flattenTemplate, but leaves line returns in place. Good for cases such as
 * formatting an address for display.
 *
 * @param strings Array of separated template strings
 * @param  values Array of values given to template string.
 *
 * @return Template string with white space stripped from each line.
 *
 * @see flattenTemplate
 */

export function formatTemplate(strings, ...values) {
  let output = '';
  values.forEach((val, i) => {
    output += (strings[i] !== undefined ? strings[i] : '') + (val !== undefined ? val : '');
  });
  output += strings[values.length];
  return output.replace(/^(\s{2})+/gm, '').trim();
}
export const formatMMtoHHMM = timeinMins => {
  const hours = timeinMins / 60;
  const mins = timeinMins % 60;
  return `${hours < 10 && '0' || ''}${hours >= 1 && Math.trunc(hours) || '0'}: ${mins < 10 && '0' || ''}${mins}`;
};
export const formatMMtoDec = timeinMins => {
  const hours = timeinMins / 60;
  return hours.toFixed(1);
};
export const formatHHMMtoMM = hhmm => {
  let [hours, minutes] = hhmm.split(':');
  hours = Number(hours);
  minutes = Number(minutes);
  return hours * 60 + minutes;
}; // YYYY-MM-DD => MM-DD-YY

export const YYYYMMDDtoMMDDYY = dateString => {
  var _dateString$split$;

  const [year, month, day] = (_dateString$split$ = dateString.split(' ')[0]) === null || _dateString$split$ === void 0 ? void 0 : _dateString$split$.split('-');
  return [month, day, year.slice(2)].join('-');
};
export const UTCtoDisplayTime = dateString => {
  if (!dateString) {
    return '';
  }

  const char = dateString.includes('T') ? 'T' : ' ';
  const [date, time] = dateString.split(char);
  return [YYYYMMDDtoMMDDYY(date), time.split(':').slice(0, 2).join(':')].join(' ');
};